/**
 * L'ENREGISTREMENT DU BROUILLON DU STUDIO, SANS RENDU — un enregistrement à la fois, à la révision relue.
 *
 * ⚠️ UN ÉCHEC NE BLOQUE JAMAIS LES SUIVANTS : la file ne garde que la version RÉSOLUE de chaque étape. Une promesse
 *    rejetée saute toute fonction qu'on lui enchaîne — un seul échec aurait fait sauter tous les enregistrements
 *    suivants, « Réessayer » compris, sans un mot.
 * ⚠️ UNE COPIE PÉRIMÉE ARRÊTE TOUT (409) : un autre poste a écrit, on n'écrase pas son travail — l'écran propose de
 *    recharger, et rien ne repart avant une relecture (`reset`).
 * ⚠️ LE DOCUMENT QUI ÉCHOUE RESTE EN ATTENTE, sauf si un plus récent l'a remplacé entre-temps : c'est lui que
 *    « Réessayer » renvoie.
 */
import type { StudioDocument } from '../api';

export type SaveState =
  | { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' }
  | { kind: 'error'; message: string }
  /** Un autre poste a modifié le brouillon : on n'écrit plus, on propose de recharger. */
  | { kind: 'conflict' };

export interface DraftSaver {
  /** Le document à enregistrer — le dernier posé l'emporte ; `null` retire ce qui attendait. */
  set(doc: StudioDocument | null): void;
  /** Enregistre ce qui attend, après ce qui est en cours. Rejette si un enregistrement de cet appel échoue. */
  flush(): Promise<void>;
  /** Un document attend-il d'être enregistré ? */
  hasPending(): boolean;
  /** Une copie périmée a arrêté les enregistrements. */
  isConflicted(): boolean;
  /** La révision à présenter à la prochaine écriture. */
  revision(): number;
  /** Repart d'une lecture neuve : révision relue, rien en attente, conflit levé. */
  reset(revision: number): void;
  /** Attend la fin de ce qui est en cours, sans en hériter l'échec. */
  settled(): Promise<void>;
}

export function createDraftSaver(opts: {
  save: (revision: number, doc: StudioDocument) => Promise<{ revision: number; dirty: boolean }>;
  isConflict: (e: unknown) => boolean;
  onState: (state: SaveState) => void;
  onSaved: (dirty: boolean) => void;
}): DraftSaver {
  let revision = 0;
  let pending: StudioDocument | null = null;
  let conflicted = false;
  let queue: Promise<void> = Promise.resolve();

  const step = async (): Promise<void> => {
    const next = pending;
    if (!next || conflicted) return;
    pending = null;
    opts.onState({ kind: 'saving' });
    try {
      const r = await opts.save(revision, next);
      revision = r.revision;
      opts.onSaved(r.dirty);
      opts.onState(pending ? { kind: 'saving' } : { kind: 'saved' });
    } catch (e) {
      if (opts.isConflict(e)) { conflicted = true; opts.onState({ kind: 'conflict' }); return; }
      pending = pending ?? next;
      opts.onState({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      throw e;
    }
  };

  const flush = (): Promise<void> => {
    const run = queue.then(step);
    queue = run.catch(() => undefined);
    // Un document posé pendant l'enregistrement part à la suite — jamais après un échec, qui attend « Réessayer ».
    return run.then(() => (pending && !conflicted ? flush() : undefined));
  };

  return {
    set: (doc) => { pending = doc; },
    flush,
    hasPending: () => pending !== null,
    isConflicted: () => conflicted,
    revision: () => revision,
    reset: (r) => { revision = r; pending = null; conflicted = false; },
    settled: () => queue,
  };
}
